<script lang="ts">
	import { onMount, setContext } from 'svelte'
	import { fetchUtils } from '../../../utils/fetchUtils'
	import AnalyticalRow from './AnalyticalRow.svelte'
	import {
		AnalyticalParentType,
		AnalyticalType,
		LoadingAnimation,
		LotState,
		LotType,
		UserPermission
	} from '../../../types/enums'
	import type { Analytical } from '../../types/analytical'
	import { openModal } from 'svelte-modals'
	import { t } from '../../../../i18n/i18n'
	import NullableDataWrapper from '../../../utils/NullableDataWrapper.svelte'
	import { attachmentsDirty, currentLot } from '../utils/fakStores'
	import Modal from '../../../organisms/Modal.svelte'
	import { derived } from 'svelte/store'
	import AnalysisAiUploadButton from '../../../organisms/AnalysisAiUploadButton.svelte'
	import Permission from '../../../menubar/Permission.svelte'
	import SecondaryButton from '../../../atoms/SecondaryButton.svelte'
	import { hideAnimation } from '../../../stores'
	import loadingSpinner from '@/animations/loading-spinner.gif'
	import infoIcon from '@/icons/icon_info_blue_outline_24px.svg'
	import InfoBanner from '../../../molecules/InfoBanner.svelte'

	setContext('useMaterialDesign', false)

	export let hasAnalyticals: boolean = false

	let lotAnalyticals: Analytical[] = []
	let analyticalType =
		$currentLot?.type === LotType.BACKFILL || $currentLot?.type === LotType.MATERIAL_NEED
			? AnalyticalType.ANALYTIC
			: AnalyticalType.ANALYSIS
	let analyticalsPending: number = 0

	let uploadUrl = ''

	onMount(() => {
		fetchAnalytics()
		fetchAnalysisStates()
	})

	const currentLotKey = derived(currentLot, ($currentLot) => ({
		id: $currentLot?.id,
		type: $currentLot?.type
	}))

	currentLotKey.subscribe(() => {
		if ($currentLot) {
			fetchAnalytics()
			fetchAnalysisStates()
			uploadUrl = `/api/lot/${$currentLot.id}/importAnalysis`
		}
	})

	async function fetchAnalytics() {
		if ($currentLot && $currentLot.id !== 'new') {
			fetchUtils
				.get(`/api/lot/${$currentLot.id}/analytics/`, false)
				.then(async (response) => {
					lotAnalyticals = response
				})
				.catch(fetchUtils.catchErrorAndShowNotification())
		}
	}

	async function fetchAnalysisStates() {
		if ($currentLot) {
			fetchUtils
				.get(`/api/mind/lot/${$currentLot.id}/analysisStatus`, false)
				.then((traces) => {
					if (traces && Array.isArray(traces)) {
						let oldPending = analyticalsPending
						analyticalsPending = traces.filter(
							(trace) => trace.state === 'IN_PROGRESS' || trace.state === 'NOT_STARTED'
						).length
						if (analyticalsPending > 0) {
							setTimeout(() => fetchAnalysisStates(), 5000)
						} else {
							hideAnimation(LoadingAnimation.AiUpload)
						}
						if (oldPending !== analyticalsPending) {
							fetchAnalytics()
						}
					}
				})
				.catch() // do nothing
		}
	}

	function createManualAnalytical() {
		openModal(Modal, {
			maxWidth: '550px',
			padding: '2.5rem 2rem 1.75rem',
			onAccept: () => {
				let origin = encodeURIComponent(window.location.href)
				const url = `/${analyticalType.toLowerCase()}?mode=manual&parentId=${$currentLot?.id}&parentType=LOT&origin=${origin}`
				window.location.assign(url)
			},
			title: $t('UI.map.editAnalysis.modal.title'),
			message: $t(`UI.map.${analyticalType.toLowerCase()}Modal.message`),
			acceptText: $t(`UI.map.modal.create${analyticalType.toLowerCase()}`)
		})
	}

	function updateAnalysesAndAttachments() {
		fetchAnalytics()
		fetchAnalysisStates()
		$attachmentsDirty = true
	}

	$: hasAnalyticals = lotAnalyticals?.length > 0 ?? false
	$: analyticalType =
		$currentLot?.type === LotType.BACKFILL || $currentLot?.type === LotType.MATERIAL_NEED
			? AnalyticalType.ANALYTIC
			: AnalyticalType.ANALYSIS
</script>

<div class="analytic-list-container">
	<NullableDataWrapper bind:data={$currentLot}>
		<div class="analytic-list-container__content">
			{#if analyticalsPending > 0}
				<InfoBanner
					text={`Deine Datei${analyticalsPending > 1 ? 'en (' + analyticalsPending + ') werden ' : ' wird '} ausgewertet.`}
					iconLeft={infoIcon}
					iconRight={loadingSpinner}
				/>
			{/if}
			{#key lotAnalyticals}
				{#if hasAnalyticals}
					<div class="analytic-table-header">
						<div>
							{$t('Analytics.testMethod')}<br />{analyticalType === AnalyticalType.ANALYSIS
								? $t('Analytics.description')
								: ''}
						</div>
						<div>
							{$t('Analytics.evaluation')}<br />{analyticalType === AnalyticalType.ANALYSIS
								? $t('Analytics.maxValue')
								: ''}
						</div>
						<div></div>
					</div>
					<div class="analytic-table scrollable">
						{#each lotAnalyticals as analytical (analytical.id)}
							<AnalyticalRow
								{analytical}
								analyticalParentType={AnalyticalParentType.LOT}
								on:updateAnalytical={() => {
									fetchAnalytics()
									fetchAnalysisStates()
								}}
							/>
						{/each}
					</div>
				{:else if analyticalType === AnalyticalType.ANALYSIS && analyticalsPending === 0}
					<Permission requiredPermissions={UserPermission.IMPORT_ANALYTICS}>
						<span class="analytic-list-empty-text">{$t('UI.map.noAnalyticals.text')}</span>
					</Permission>
				{/if}
			{/key}
		</div>
		<div class="analytic-add-buttons">
			<SecondaryButton
				label={$t('UI.map.createAnalytical.manual')}
				disabled={$currentLot?.state === LotState.DISCARDED}
				fullWidth
				on:click={createManualAnalytical}
			/>
			{#if analyticalType === AnalyticalType.ANALYSIS}
				<Permission requiredPermissions={UserPermission.IMPORT_ANALYTICS}>
					<AnalysisAiUploadButton
						{uploadUrl}
						disabled={$currentLot?.state === LotState.DISCARDED}
						updateFun={updateAnalysesAndAttachments}
					/>
				</Permission>
			{/if}
		</div>
	</NullableDataWrapper>
</div>

<style lang="scss">
	@use '$styles/global';

	.analytic-list-container {
		width: 100%;

		@include global.flex-col(0.875rem, $alignment: flex-end);

		padding: 0 1.5625rem 1.5rem;
	}

	.analytic-list-container__content {
		width: 100%;
		max-height: 18rem;
		display: flex;
		flex-direction: column;
	}

	.analytic-table {
		position: relative;
		width: 100%;
		border: none;
		border-collapse: separate;
		border-spacing: 0;
		display: grid;
		grid-template-columns: minmax(0, 3fr) minmax(0, 3fr) minmax(0, 1fr);
		column-gap: 0.625rem;
		max-height: 15.125rem;
		overflow-y: auto;
		padding-right: 0.5rem;

		// Should reserve some space for the scrollbar, which could potentially fix the safari overlapping scrollbar issue
		scrollbar-gutter: auto;
	}

	.analytic-table-header {
		@include global.roboto-font(1.125rem, 500, 0.75rem);

		display: grid;
		grid-template-columns: minmax(0, 3fr) minmax(0, 3fr) minmax(0, 1fr);
		border: none;
		padding: 0.5rem 0.75rem 0.5rem 0;
		background: white;
		border-bottom: 1px solid global.$primaryGreen;
	}

	.analytic-add-buttons {
		@include global.flex-row(0.625rem, $alignment: flex-end);
		padding-top: 0.75rem;
	}

	span.analytic-list-empty-text {
		@include global.roboto-font(1.25rem, 400, 0.875rem);
	}
</style>
